import { exportCode, importCode, downloadSave, hasSaveableProgress } from "../core/SaveCode";

/**
 * Modal "Guardar y continuar": genera un CÓDIGO de progreso para copiar/guardar
 * y permite pegar uno para continuar en cualquier ordenador. Pensado para el
 * aula (equipos compartidos que borran los datos, alumnado que cambia de PC).
 * Autónomo: crea su propio DOM y estilos; se usa desde la portada y el menú.
 */
export function openProgressModal(): void {
  injectStyles();
  const code = exportCode();
  const has = hasSaveableProgress();

  const back = document.createElement("div");
  back.id = "epi-progress";
  back.className = "gp-back";
  back.innerHTML = `
    <div class="gp-card">
      <div class="gp-head"><span class="gp-tag"></span>GUARDAR Y CONTINUAR</div>
      <div class="gp-body">
        <p class="gp-intro">Tu progreso se guarda en <b>este ordenador</b>. Para seguir en <b>otro equipo</b> —o si el centro borra los datos al cerrar sesión— usa tu <b>código de progreso</b>.</p>

        <div class="gp-sec">
          <h3>1 · Guarda tu progreso</h3>
          ${has
            ? `<p class="gp-note">Copia este código y guárdalo donde puedas recuperarlo: un correo para ti, una nota del móvil, o descárgalo como archivo.</p>
               <textarea class="gp-code" id="gp-code" readonly rows="3">${code}</textarea>
               <div class="gp-row">
                 <button class="gp-btn gp-btn-main" id="gp-copy">Copiar código</button>
                 <button class="gp-btn gp-btn-ghost" id="gp-download">Descargar archivo</button>
               </div>`
            : `<p class="gp-note">Aún no has empezado ninguna misión. Cuando avances, aquí tendrás tu código para continuar en otro equipo.</p>`}
        </div>

        <div class="gp-sec">
          <h3>2 · Continuar con un código</h3>
          <p class="gp-note">¿Vienes de otro ordenador? Pega aquí tu código (o carga tu archivo) y sigue donde lo dejaste.</p>
          <textarea class="gp-code" id="gp-paste" rows="3" placeholder="Pega aquí tu código EPI1-..."></textarea>
          <div class="gp-row">
            <button class="gp-btn gp-btn-main" id="gp-load">Continuar aquí</button>
            <button class="gp-btn gp-btn-ghost" id="gp-file">Cargar desde archivo</button>
            <input type="file" id="gp-fileinput" accept=".txt,text/plain" hidden />
          </div>
          <p class="gp-msg" id="gp-msg"></p>
        </div>
      </div>
      <div class="gp-foot"><button class="gp-btn gp-btn-close" id="gp-close">Cerrar</button></div>
    </div>`;
  document.body.appendChild(back);

  const $ = <T extends HTMLElement>(id: string): T | null => back.querySelector<T>("#" + id);
  const msg = $("gp-msg")!;
  const showMsg = (text: string, kind: "ok" | "err"): void => {
    msg.textContent = text;
    msg.className = "gp-msg gp-msg-" + kind;
  };
  const close = (): void => back.remove();

  $("gp-close")!.addEventListener("click", close);
  back.addEventListener("click", (e) => { if (e.target === back) close(); });

  $("gp-copy")?.addEventListener("click", async () => {
    const ta = $<HTMLTextAreaElement>("gp-code")!;
    try {
      await navigator.clipboard.writeText(ta.value);
    } catch {
      ta.focus(); ta.select();
      try { document.execCommand("copy"); } catch { /* nada */ }
    }
    showMsg("Código copiado. Guárdalo bien.", "ok");
  });

  $("gp-download")?.addEventListener("click", () => {
    downloadSave();
    showMsg("Archivo descargado. Guárdalo para el próximo día.", "ok");
  });

  const tryLoad = (raw: string): void => {
    if (importCode(raw)) {
      showMsg("¡Progreso cargado! Reiniciando…", "ok");
      window.setTimeout(() => location.reload(), 700);
    } else {
      showMsg("Ese código no es válido. Cópialo entero, incluido el «EPI1-».", "err");
    }
  };

  $("gp-load")?.addEventListener("click", () => {
    tryLoad($<HTMLTextAreaElement>("gp-paste")!.value);
  });

  $("gp-file")?.addEventListener("click", () => $<HTMLInputElement>("gp-fileinput")!.click());
  $<HTMLInputElement>("gp-fileinput")?.addEventListener("change", (e) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => tryLoad(String(reader.result || ""));
    reader.onerror = () => showMsg("No se pudo leer el archivo.", "err");
    reader.readAsText(file);
  });
}

function injectStyles(): void {
  if (document.getElementById("gp-styles")) return;
  const tape = "repeating-linear-gradient(45deg,#f0c400 0 12px,#171308 12px 24px)";
  const css = `
  .gp-back{position:fixed;inset:0;z-index:120;background:rgba(10,8,4,.72);display:flex;align-items:center;justify-content:center;padding:18px;font-family:"Segoe UI",system-ui,sans-serif}
  .gp-card{background:#efe3cb;border:2px solid #241d14;border-radius:6px;max-width:560px;width:100%;max-height:90vh;display:flex;flex-direction:column;overflow:hidden;box-shadow:0 22px 56px rgba(0,0,0,.6)}
  .gp-head{background:#185fa5;color:#fff;font-weight:800;font-size:1rem;letter-spacing:.05em;padding:.72rem 1rem;display:flex;align-items:center;gap:.5rem}
  .gp-tag{width:14px;height:14px;background:${tape};box-shadow:0 0 0 1px rgba(0,0,0,.3)}
  .gp-body{padding:1rem;overflow:auto;color:#241d14}
  .gp-intro{margin:.1rem 0 .9rem;line-height:1.5}
  .gp-intro b{color:#171308}
  .gp-sec{border-top:1px solid #d3c39d;padding-top:.8rem;margin-top:.6rem}
  .gp-sec:first-of-type{border-top:none;margin-top:0;padding-top:0}
  .gp-sec h3{margin:0 0 .35rem;font-size:.95rem;color:#8a2018;text-transform:uppercase;letter-spacing:.04em}
  .gp-note{margin:.1rem 0 .55rem;font-size:.9rem;line-height:1.45;color:#5a4a2c}
  .gp-code{width:100%;box-sizing:border-box;font-family:"Consolas","Courier New",monospace;font-size:.82rem;line-height:1.35;border:1.5px solid #b0a891;border-radius:5px;background:#fbf6ea;color:#241d14;padding:.55rem;resize:vertical;word-break:break-all}
  .gp-row{display:flex;gap:.5rem;flex-wrap:wrap;margin-top:.55rem}
  .gp-btn{border:2px solid #241d14;border-radius:7px;padding:.55em 1.1em;font-family:inherit;font-weight:800;font-size:.9rem;cursor:pointer;text-transform:uppercase;letter-spacing:.02em}
  .gp-btn-main{background:#f0c400;color:#171308;box-shadow:2px 2px 0 rgba(0,0,0,.35)}
  .gp-btn-main:hover{background:#ffd21f}
  .gp-btn-ghost{background:#e6d7ba;color:#241d14;border-color:#8a785a}
  .gp-btn-ghost:hover{background:#dcc9a3}
  .gp-msg{min-height:1.1em;margin:.6rem 0 0;font-size:.88rem;font-weight:700}
  .gp-msg-ok{color:#1e6b3f}
  .gp-msg-err{color:#b23a2a}
  .gp-foot{padding:.7rem 1rem;background:#e6d7ba;display:flex;justify-content:flex-end;border-top:1px solid #cbb891}
  .gp-btn-close{background:#241d14;color:#f4ead0;border-color:#241d14}
  `;
  const style = document.createElement("style");
  style.id = "gp-styles";
  style.textContent = css;
  document.head.appendChild(style);
}
